import { useState, useRef, useEffect } from 'react'
import { clsx } from 'clsx'

export interface MultiSelectOption {
  id: string
  label: string
}

export interface MultiSelectProps {
  options: MultiSelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  searchable?: boolean
  disabled?: boolean
  error?: string
  className?: string
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select options...',
  searchable = true,
  disabled = false,
  error,
  className,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase())
  )

  const selectedOptions = options.filter((option) => value.includes(option.id))

  const handleSelect = (optionId: string) => {
    if (value.includes(optionId)) {
      onChange(value.filter((id) => id !== optionId))
    } else {
      onChange([...value, optionId])
    }
    setSearch('')
    inputRef.current?.focus()
  }

  const handleRemove = (optionId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(value.filter((id) => id !== optionId))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      setSearch('')
    }
  }

  return (
    <div className={clsx('relative w-full', className)} ref={containerRef}>
      <div
        className={clsx(
          'input min-h-[42px] flex flex-wrap gap-1.5 items-center cursor-text p-2',
          disabled && 'opacity-50 cursor-not-allowed',
          error && 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500/20',
          isOpen && 'ring-2 ring-primary-500/20 border-primary-500'
        )}
        onClick={() => {
          if (!disabled) {
            setIsOpen(true)
            inputRef.current?.focus()
          }
        }}
      >
        {selectedOptions.map((option) => (
          <span
            key={option.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-sm"
          >
            {option.label}
            {!disabled && (
              <button
                type="button"
                className="hover:text-primary-900 focus:outline-none"
                onClick={(e) => handleRemove(option.id, e)}
              >
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </span>
        ))}
        {searchable && (
          <input
            ref={inputRef}
            type="text"
            className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-sm p-0 focus:ring-0"
            placeholder={selectedOptions.length === 0 ? placeholder : ''}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
          />
        )}
        {!searchable && selectedOptions.length === 0 && (
          <span className="text-secondary-400 text-sm">{placeholder}</span>
        )}
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-secondary-500">No options found</div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = value.includes(option.id)
              return (
                <div
                  key={option.id}
                  className={clsx(
                    'px-3 py-2 cursor-pointer flex items-center justify-between',
                    isSelected ? 'bg-primary-50 text-primary-700' : 'hover:bg-secondary-50'
                  )}
                  onClick={() => handleSelect(option.id)}
                >
                  <span className="text-sm">{option.label}</span>
                  {isSelected && (
                    <svg className="h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  )
}
